import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import slugify from 'slugify';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import Byline from '~components/base/Bylines';
import GlobalStyles from '../styles/GlobalStyles';
import useBusiness from '~lib/useBusiness';
import BCHLogo from '../assets/images/bch-logo.jpg';

export const pageQuery = graphql`
    query {
        site {
            siteMetadata {
                title
                sectionName
                projectName
            }
        }
        story: arcStory(storyId: {eq: "6UHXOEMQWFH7RGFMD2MZ2LMN54"}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
                url
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              linkData {
                image
                description
              }
              citation {
                content
              }
            }
            promoImage {
              url
              srcSet
              caption
              credit {
                name
              }
            }
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: {
    story,
    site: {
      siteMetadata: { sectionName, projectName },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: `apps.${story.slug}`,
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);

  // testing for presence of publishDate archie element
  // if it exists, set the pubDateObj to it's value and pass to the Byline component below
  // if it doesn't pass null
  let pubDateObj = null;
  story.bodyElements.forEach((e) => {
    if (e.archieJson) {
      const parsedArchie = JSON.parse(e.archieJson);
      if (parsedArchie.publishDate !== undefined) {
        pubDateObj = parsedArchie;
      }
    }
  });

  return (
    <Layout>
      <GlobalStyles />
      <Topper>
        <img
          className="hero__image"
          srcSet={get(story, 'promoImage.srcSet')}
          src={get(story, 'promoImage.url')}
          alt={get(story, 'promoImage.caption')}
          sizes="(max-width: 240px) 480vw, (max-width: 320px) 640vw, (max-width: 600px), 1024vw, 2048vw"
        />
        <div className="hero__type">
          <h1 className="headline millerbanner-n4">{get(story, 'headlines.basic')}</h1>
          {subhed}
          <Byline
            data={get(story, 'credits')}
            pubDate={pubDateObj}
          />
        </div>
      </Topper>
      <div className="supported-by">
        <h6 className="bentonsanscond-n6">Supported by</h6>
        <a href="https://www.childrenshospital.org/" rel="noreferrer" target="_blank">
          <img src={BCHLogo} alt="Boston Children's Hospital logo. Text includes 'Where the world comes for answers'" />
        </a>
      </div>
      {story.bodyElements.map((element) => {
        if (element.archieJson) {
          return (
            <ArchieElement
              data={element}
              key={element._id}
              relatedLinks={get(story, 'relatedLinks')}
            />
          );
        }
        return (
          <BodyElement
            data={element}
            key={element._id}
          />
        );
      })}
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
